import { useEffect, useState } from 'react'
import { AnimatePresence, motion } from 'framer-motion'
import { usePrefersReducedMotion } from '@/lib/useReducedMotion'

/* ================================================================== */
/*  Independence Day loader — a one-shot festive splash on 15 August.   */
/*  Plays once per session on the homepage, then fades to reveal the    */
/*  site. Shows the client's festive lockup on black (as designed).     */
/* ================================================================== */

/** True only on 15 August (any year), in the visitor's local time. */
export function isIndependenceDay(d: Date = new Date()) {
  return d.getMonth() === 7 && d.getDate() === 15 // month is 0-indexed → 7 = August
}

/** QA override: `?iday` in the URL forces the loader on any date. */
function forcedPreview() {
  try {
    return new URLSearchParams(window.location.search).has('iday')
  } catch {
    return false
  }
}

const SEEN_KEY = 'lc_iday_seen'

export function IndependenceDayLoader() {
  const reduced = usePrefersReducedMotion()
  // Decide once, up front: only on 15 Aug and only the first homepage load
  // this session. (Reads storage; the write happens in the effect below.)
  const [show, setShow] = useState(() => {
    if (forcedPreview()) return true // ?iday always plays, ignores the once-per-session flag
    if (!isIndependenceDay()) return false
    try {
      return sessionStorage.getItem(SEEN_KEY) !== '1'
    } catch {
      return true
    }
  })

  useEffect(() => {
    if (!show) return
    if (!forcedPreview()) {
      try {
        sessionStorage.setItem(SEEN_KEY, '1')
      } catch {
        /* ignore */
      }
    }
    // Timer is tied to `show`, not the seen-check, so a StrictMode
    // mount→cleanup→mount cycle always re-arms the auto-dismiss.
    const t = setTimeout(() => setShow(false), reduced ? 1200 : 2800)
    return () => clearTimeout(t)
  }, [show, reduced])

  return (
    <AnimatePresence>
      {show && (
        <motion.div
          key="iday-loader"
          className="fixed inset-0 z-[100] grid place-items-center overflow-hidden bg-black px-6"
          initial={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.6, ease: 'easeInOut' }}
        >
          <motion.img
            src="/brand/lifecraft-independence.jpg"
            alt="Happy Independence Day from LifeCraft4U"
            className="w-[min(90vw,660px)] select-none"
            draggable={false}
            initial={reduced ? { opacity: 0 } : { opacity: 0, scale: 0.92 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: reduced ? 0.5 : 1.1, ease: [0.22, 1, 0.36, 1] }}
          />
        </motion.div>
      )}
    </AnimatePresence>
  )
}
